import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, Copy, Globe2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';

export function IntakeSettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const hospitalId = user?.hospital_id ?? '';
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);

  const settings = useQuery({
    queryKey: ['intake-settings', hospitalId],
    queryFn: () => api.intake.getSettings(hospitalId),
    enabled: !!hospitalId,
  });

  const toggle = useMutation({
    mutationFn: (next: boolean) => api.intake.setPublicEnabled(hospitalId, next),
    onSuccess: (_data, next) => {
      toast.success(next ? 'Public form is live' : 'Public form disabled');
      qc.invalidateQueries({ queryKey: ['intake-settings', hospitalId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const url = `${window.location.origin}${import.meta.env.BASE_URL.replace(/\/$/, '')}/intake/public/${encodeURIComponent(hospitalId)}`;
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  const enabled = settings.data?.intake_enabled === true;
  const publicEnabled = Boolean(settings.data?.public_intake_enabled);

  return (
    <div className="px-5 py-5 max-w-lg lg:max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => navigate('/settings')}
          className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h2 className="font-sans text-lg font-extrabold text-foreground leading-none">Intake</h2>
      </div>

      {!enabled ? (
        <Card className="border-dashed p-6 text-center">
          <p className="text-sm text-muted-foreground">
            Direct Intake is not enabled for this hospital. Contact RefDash operations.
          </p>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Globe2 className="w-4 h-4 text-primary" />
              <CardTitle>Public referral form</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                Anyone with the link can submit a referral request; every submission
                enters mandatory triage. Disabling it never affects private patient
                invitations.
              </p>
              <Switch
                checked={publicEnabled}
                disabled={toggle.isPending}
                onCheckedChange={(next) => toggle.mutate(next)}
                aria-label="Enable public referral form"
              />
            </div>
            {publicEnabled && (
              <>
                <div className="mx-auto w-fit rounded-2xl border border-border bg-white p-4">
                  <QRCodeSVG value={url} size={180} />
                </div>
                <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                  <code className="min-w-0 flex-1 break-all text-xs" dir="ltr">{url}</code>
                  <Button variant="outline" size="sm" onClick={copy} className="shrink-0 gap-1.5">
                    {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
